import React, { useLayoutEffect, useRef, useState } from 'react';

type Props = {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  label?: string;
  previewHtml?: string;
  previewEnabled?: boolean;
  placeholders?: string[];
  paperMode?: boolean;
  placeholder?: string;
};

const PLACEHOLDERS = [
  'employeeName',
  'overtureSignerName',
  'startDate',
  'effectiveDate',
  'hourlyRate',
  'aideRate',
  'lessonFee',
  'trainingFee',
  'overtureSignDate',
  'employeeSignDate',
  'overtureSignature',
  'employeeSignature',
];

/** Document-style rich-text editor with a letter-sized PDF preview. */
export default function HtmlDocumentEditor({
  value,
  onChange,
  minHeight = 360,
  label,
  previewHtml,
  previewEnabled = true,
  placeholders = PLACEHOLDERS,
  paperMode = true,
  placeholder,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastExternal = useRef<string>('');
  const savedRange = useRef<Range | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  useLayoutEffect(() => {
    if (mode !== 'edit' || !ref.current) return;
    // The editable node is unmounted while Preview is open. Repopulate the
    // newly mounted node even when the underlying value has not changed.
    if (ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    lastExternal.current = value || '';
  }, [mode, value]);

  const rememberSelection = () => {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !ref.current) return;
    const range = selection.getRangeAt(0);
    if (ref.current.contains(range.commonAncestorContainer)) {
      savedRange.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    if (!savedRange.current) return;
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(savedRange.current);
  };

  const emitChange = () => {
    if (!ref.current) return;
    const html = ref.current.innerHTML;
    lastExternal.current = html;
    onChange(html);
  };

  const run = (command: string, arg?: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(command, false, arg);
    rememberSelection();
    emitChange();
  };

  const insertPlaceholder = (key: string) => {
    run('insertText', `{{${key}}}`);
  };

  const btn: React.CSSProperties = {
    border: '1px solid #cbd5e1',
    background: '#fff',
    borderRadius: 6,
    padding: '7px 10px',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <div style={{ border: '1px solid #cbd5e1', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
      <style>{`
        .hiring-document-editor h1, .hiring-document-preview h1 { font: 700 20px/1.2 Arial, sans-serif; margin: 0 0 22px; }
        .hiring-document-editor h2, .hiring-document-preview h2 { font: 700 14px/1.25 Arial, sans-serif; margin: 18px 0 4px; }
        .hiring-document-editor h3, .hiring-document-preview h3 { font: 700 13px/1.25 Arial, sans-serif; margin: 12px 0 4px; }
        .hiring-document-editor p, .hiring-document-preview p { margin: 0 0 13px; }
        .hiring-document-editor ul, .hiring-document-editor ol,
        .hiring-document-preview ul, .hiring-document-preview ol { margin: 0 0 13px; padding-left: 24px; }
        .hiring-document-editor li, .hiring-document-preview li { margin-bottom: 4px; }
        .hiring-document-editor table, .hiring-document-preview table { width: 100%; margin: 20px 0; border-collapse: collapse; }
        .hiring-document-editor td, .hiring-document-preview td { width: 50%; padding-right: 32px; vertical-align: top; }
        .hiring-document-editor img, .hiring-document-preview img { max-width: 180px; max-height: 60px; object-fit: contain; }
        .hiring-document-editor:empty::before { content: attr(data-placeholder); color: #94a3b8; pointer-events: none; }
      `}</style>
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <strong>{label || 'Document'}</strong>
        {previewEnabled && (
          <div style={{ display: 'flex', padding: 3, borderRadius: 8, background: '#e2e8f0' }}>
            {(['edit', 'preview'] as const).map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setMode(item)}
                style={{
                  border: 0,
                  borderRadius: 6,
                  padding: '6px 12px',
                  background: mode === item ? '#fff' : 'transparent',
                  color: mode === item ? '#0f172a' : '#64748b',
                  boxShadow: mode === item ? '0 1px 3px rgba(15,23,42,.15)' : 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                  textTransform: 'capitalize',
                }}
              >
                {item}
              </button>
            ))}
          </div>
        )}
      </div>

      {mode === 'edit' ? (
        <>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              padding: 8,
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('bold')}>
              <strong>B</strong>
            </button>
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('italic')}>
              <em>I</em>
            </button>
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('insertUnorderedList')}>
              • List
            </button>
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('formatBlock', 'h1')}>
              Title
            </button>
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('formatBlock', 'h2')}>
              Heading
            </button>
            <button type="button" style={btn} onMouseDown={(e) => e.preventDefault()} onClick={() => run('formatBlock', 'p')}>
              Body
            </button>
            {placeholders.length > 0 && (
              <>
                <span style={{ width: 1, height: 26, background: '#cbd5e1', margin: '0 4px' }} />
                <select
                  defaultValue=""
                  aria-label="Insert variable"
                  onMouseDown={rememberSelection}
                  onChange={(e) => {
                    if (e.target.value) insertPlaceholder(e.target.value);
                    e.target.value = '';
                  }}
                  style={{ ...btn, minWidth: 190 }}
                >
                  <option value="" disabled>
                    Insert variable…
                  </option>
                  {placeholders.map((key) => (
                    <option key={key} value={key}>
                      {`{{${key}}}`}
                    </option>
                  ))}
                </select>
              </>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b' }}>
              Click in the page and type normally
            </span>
          </div>
          <div
            style={{
              padding: paperMode ? 24 : 0,
              background: paperMode ? '#e2e8f0' : '#fff',
              overflowX: 'auto',
            }}
          >
            <div
              ref={ref}
              className="hiring-document-editor"
              data-placeholder={placeholder || ''}
              contentEditable
              suppressContentEditableWarning
              onKeyUp={rememberSelection}
              onMouseUp={rememberSelection}
              onInput={() => {
                rememberSelection();
                emitChange();
              }}
              style={{
                boxSizing: 'border-box',
                width: paperMode ? 'min(100%, 816px)' : '100%',
                minWidth: paperMode ? 640 : 0,
                minHeight,
                margin: '0 auto',
                padding: paperMode ? '58px 68px' : '16px 18px',
                outline: 'none',
                background: '#fff',
                boxShadow: paperMode ? '0 4px 18px rgba(15,23,42,.14)' : 'none',
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: 14,
                fontWeight: 400,
                lineHeight: 1.42,
                color: '#111827',
              }}
            />
          </div>
        </>
      ) : (
        <div style={{ padding: 24, background: '#e2e8f0', overflowX: 'auto' }}>
          <div
            className="hiring-document-preview"
            style={{
              boxSizing: 'border-box',
              width: 816,
              minHeight: 1056,
              margin: '0 auto',
              padding: '58px 68px',
              background: '#fff',
              boxShadow: '0 4px 18px rgba(15,23,42,.14)',
              color: '#111827',
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: 14,
              lineHeight: 1.42,
            }}
            dangerouslySetInnerHTML={{ __html: previewHtml || value }}
          />
        </div>
      )}
    </div>
  );
}
